import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/profile.pro.css';
import { ensureValidSession, getApiBaseUrl, getStoredUser, logoutUser, setStoredUser } from '../utils/auth';

const SEARCHABLE_ITEMS = [
  { title: 'Información de la Cuenta', category: 'Cuenta', icon: '👤', section: 'account-info-section' },
  { title: 'Datos Personales', category: 'Cuenta', icon: '📝', section: 'account-info-section' },
  { title: 'Métodos de Pago', category: 'Pagos', icon: '💳', section: 'payment-methods-section' },
  { title: 'Agregar Tarjeta', category: 'Pagos', icon: '➕', section: 'payment-methods-section' },
  { title: 'Historial de Pagos', category: 'Pagos', icon: '📊', section: 'payment-history-section' },
  { title: 'Descargar Factura', category: 'Pagos', icon: '📥', section: 'payment-history-section' },
  { title: 'Plan Activo', category: 'Suscripción', icon: '💎', section: 'active-plan-section' },
  { title: 'Cerrar Sesión', category: 'Seguridad', icon: '🚪', section: 'logout-section' }
];

const EMPTY_PROFILE = {
  id: null,
  name: '',
  email: '',
  company: '',
  is_company: false,
  rut: '',
  company_rut: '',
  city: '',
  region: '',
  address: '',
  personal_address: '',
  company_address: '',
  origen: '',
  created_at: ''
};

const PROFILE_ENDPOINTS = ['/profile/me', '/api/profile/me'];

function getStoredAddress(user) {
  return user?.address
    || user?.personal_address
    || user?.company_address
    || user?.direccionParticular
    || user?.direccionEmpresa
    || '';
}

function getStoredRut(user) {
  return user?.rut || user?.rutPersonal || '';
}

function getStoredCompanyRut(user) {
  return user?.company_rut || user?.companyRut || user?.rutEmpresa || '';
}

async function requestProfile(sessionToken, method = 'GET', body) {
  const baseUrl = getApiBaseUrl();
  let lastResponse = null;
  let lastData = {};

  for (let index = 0; index < PROFILE_ENDPOINTS.length; index += 1) {
    const endpoint = PROFILE_ENDPOINTS[index];
    const response = await fetch(`${baseUrl}${endpoint}`, {
      method,
      headers: {
        ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${sessionToken}`
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });

    const data = await response.json().catch(() => ({}));
    lastResponse = response;
    lastData = data;

    if (response.status !== 404 || index === PROFILE_ENDPOINTS.length - 1) {
      return { response, data, endpoint };
    }
  }

  return { response: lastResponse, data: lastData, endpoint: PROFILE_ENDPOINTS[0] };
}

function normalizeProfile(profile) {
  if (!profile || typeof profile !== 'object') {
    return EMPTY_PROFILE;
  }

  return {
    id: profile.id ?? profile.backendId ?? profile.user_id ?? null,
    name: (profile.name || '').toString(),
    email: (profile.email || '').toString(),
    company: (profile.company || '').toString(),
    is_company: Boolean(profile.is_company),
    rut: getStoredRut(profile).toString(),
    company_rut: getStoredCompanyRut(profile).toString(),
    city: (profile.city || profile.ciudadParticular || '').toString(),
    region: (profile.region || profile.regionParticular || '').toString(),
    address: getStoredAddress(profile).toString(),
    personal_address: (profile.personal_address || profile.direccionParticular || '').toString(),
    company_address: (profile.company_address || profile.direccionEmpresa || '').toString(),
    origen: (profile.origen || '').toString(),
    created_at: (profile.created_at || '').toString()
  };
}

function buildInitials(name, email) {
  const source = (name || email || 'Usuario').trim();
  const parts = source.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return 'US';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function formatMemberSince(dateValue) {
  if (!dateValue) {
    return 'Miembro recientemente';
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return 'Miembro recientemente';
  }

  return `Miembro desde ${parsed.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'long'
  })}`;
}

function mapProfileToStoredUser(profile, previousUser) {
  return {
    ...(previousUser || {}),
    id: profile.id ?? previousUser?.id ?? null,
    backendId: profile.id ?? previousUser?.backendId ?? null,
    name: profile.name,
    email: profile.email,
    company: profile.company || '',
    is_company: profile.is_company ? 1 : 0,
    rut: profile.rut || '',
    rutPersonal: profile.rut || '',
    company_rut: profile.company_rut || '',
    companyRut: profile.company_rut || '',
    rutEmpresa: profile.company_rut || '',
    city: profile.city || '',
    ciudadParticular: profile.city || '',
    region: profile.region || '',
    regionParticular: profile.region || '',
    address: profile.address || '',
    personal_address: profile.personal_address || profile.address || '',
    direccionParticular: profile.personal_address || profile.address || '',
    company_address: profile.company_address || profile.address || '',
    direccionEmpresa: profile.company_address || profile.address || '',
    origen: profile.origen || previousUser?.origen || '',
    created_at: profile.created_at || previousUser?.created_at || ''
  };
}

function extractErrorMessage(payload, fallback) {
  return payload?.error || payload?.message || fallback;
}

export default function Profile() {
  const navigate = useNavigate();
  const storedUser = getStoredUser();
  const initialProfile = normalizeProfile(storedUser);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [logoutModal, setLogoutModal] = useState(false);
  const [pwdSending, setPwdSending] = useState(false);
  const [pwdSent, setPwdSent] = useState(false);
  const [pwdTimeLeft, setPwdTimeLeft] = useState(0);
  const [pwdTimerId, setPwdTimerId] = useState(null);
  const [pageError, setPageError] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profile, setProfile] = useState(initialProfile);
  const [formData, setFormData] = useState(initialProfile);

  useEffect(() => {
    if (!query.trim()) {
      setSearchActive(false);
      setResults([]);
      return;
    }

    const normalizedQuery = query.toLowerCase().trim();
    const filtered = SEARCHABLE_ITEMS.filter((item) => (
      item.title.toLowerCase().includes(normalizedQuery)
      || item.category.toLowerCase().includes(normalizedQuery)
    ));

    setResults(filtered);
    setSearchActive(true);
  }, [query]);

  useEffect(() => {
    return () => {
      if (pwdTimerId) clearInterval(pwdTimerId);
    };
  }, [pwdTimerId]);

  useEffect(() => {
    let isMounted = true;

    async function loadProfile() {
      setIsLoadingProfile(true);
      setPageError('');

      const session = await ensureValidSession();
      if (!session.ok || !session.token) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const { response, data } = await requestProfile(session.token, 'GET');

        if (!response.ok) {
          const message = extractErrorMessage(data, 'No se pudo cargar tu perfil.');
          if (response.status === 401) {
            navigate('/login', { replace: true });
            return;
          }

          throw new Error(message);
        }

        if (!isMounted) {
          return;
        }

        const nextProfile = normalizeProfile(data.profile);
        setProfile(nextProfile);
        setFormData(nextProfile);
        setStoredUser(mapProfileToStoredUser(nextProfile, getStoredUser()));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setPageError(error.message || 'No se pudo cargar tu perfil.');
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    }

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  function showToast(message, type = 'success') {
    setToast({ show: true, message, type });
    window.setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  }

  function navigateToSection(sectionId) {
    const section = document.getElementById(sectionId);
    if (!section) {
      return;
    }

    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSearchActive(false);
    setQuery('');
    section.style.transition = 'background 0.5s';
    section.style.background = '#fef5f1';
    window.setTimeout(() => {
      section.style.background = '';
    }, 1000);
  }

  function handleFieldChange(event) {
    const { id, value } = event.target;
    setFormData((current) => ({
      ...current,
      [id]: value
    }));
  }

  async function handleSaveAccount(event) {
    event.preventDefault();
    setIsSavingProfile(true);
    setPageError('');

    const session = await ensureValidSession();
    if (!session.ok || !session.token) {
      setIsSavingProfile(false);
      navigate('/login', { replace: true });
      return;
    }

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      company: formData.company.trim(),
      rut: formData.rut.trim(),
      company_rut: formData.company_rut.trim(),
      city: formData.city.trim(),
      region: formData.region.trim(),
      address: formData.address.trim(),
      is_company: Boolean(formData.company.trim())
    };

    try {
      const { response, data } = await requestProfile(session.token, 'PUT', payload);

      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'No se pudo actualizar tu perfil.'));
      }

      const nextProfile = normalizeProfile(data.profile);
      setProfile(nextProfile);
      setFormData(nextProfile);
      setStoredUser(mapProfileToStoredUser(nextProfile, getStoredUser()));
      showToast(data.message || 'Perfil actualizado correctamente');
    } catch (error) {
      const message = error.message || 'No se pudo actualizar tu perfil.';
      setPageError(message);
      showToast(message, 'error');
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function handleLogoutConfirm() {
    setLogoutModal(false);
    await logoutUser();
    showToast('Sesión cerrada. Redirigiendo...');
    window.setTimeout(() => navigate('/login', { replace: true }), 1000);
  }

  function startPwdTimer(seconds = 60) {
    setPwdTimeLeft(seconds);
    if (pwdTimerId) clearInterval(pwdTimerId);
    const id = setInterval(() => {
      setPwdTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(id);
          setPwdSent(false);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    setPwdTimerId(id);
  }

  async function handleChangePassword() {
    if (!profile?.email) {
      showToast('No hay correo asociado a esta cuenta', 'error');
      return;
    }

    if (pwdTimeLeft > 0) {
      showToast('Ya se envió una solicitud recientemente. Espera un momento.', 'info');
      return;
    }

    setPwdSending(true);
    try {
      const url = `${getApiBaseUrl()}/api/recover`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email })
      });
      const data = await res.json().catch(() => ({}));

      setPwdSent(true);
      startPwdTimer(60);
      if (data && data.message) showToast(data.message, 'success');
      else showToast('Si existe la cuenta, hemos enviado un enlace para cambiar la contraseña.', 'success');
    } catch (err) {
      showToast('No se pudo solicitar cambio de contraseña. Intenta nuevamente.', 'error');
    } finally {
      setPwdSending(false);
    }
  }

  const avatarInitials = buildInitials(profile.name, profile.email);
  const memberSince = formatMemberSince(profile.created_at);
  const primaryIdentifier = profile.is_company && profile.company_rut
    ? profile.company_rut
    : (profile.rut || (profile.id ? `ID ${profile.id}` : 'No registrado'));

  return (
    <div className="profile-wrapper">
      <div className="profile-container">
        <div className="profile-header">
          <div className="header-top">
            <div className="header-left">
              <img src="/logo512.png" alt="Ker Logo" className="logo-icon" />
              <div>
                <div className="company-name" id="company-name">Ker</div>
                <h1 className="profile-title" id="profile-title">Mi Perfil</h1>
              </div>
            </div>

            <div style={{ flex: 1, marginLeft: '1rem' }}>
              <div className="search-container">
                <span className="search-icon">🔍</span>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  type="text"
                  className="search-input"
                  placeholder="Buscar secciones, opciones, ajustes..."
                  autoComplete="off"
                />

                <div className={`search-results ${searchActive ? 'active' : ''}`}>
                  {results.length === 0 ? (
                    <div className="no-results">No se encontraron resultados</div>
                  ) : (
                    results.map((item) => (
                      <div key={`${item.section}-${item.title}`} className="search-result-item" onClick={() => navigateToSection(item.section)}>
                        <span className="search-result-icon">{item.icon}</span>
                        <div className="search-result-text">
                          <div className="search-result-title">{item.title}</div>
                          <div className="search-result-category">{item.category}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="user-card">
          <div className="user-info-top">
            <div className="user-avatar" id="user-avatar">{avatarInitials}</div>
            <div className="user-details">
              <h2 className="user-name" id="user-name">{profile.name || 'Usuario'}</h2>
              <p className="user-email" id="user-email">{profile.email || 'Sin correo electrónico'}</p>
              <p className="user-member-since">{memberSince}</p>
              <div className="profile-meta-row">
                <span className="profile-pill">{profile.is_company ? 'Cuenta empresa' : 'Cuenta personal'}</span>
                {profile.origen ? <span className="profile-pill is-muted">Origen: {profile.origen}</span> : null}
              </div>
            </div>
            <div style={{display:'flex', gap:'0.6rem', alignItems:'center'}}>
              <button
                className="edit-profile-btn"
                onClick={() => navigateToSection('account-info-section')}
                type="button"
              >
                ✏️ Editar Perfil
              </button>
              <button
                className="edit-profile-btn"
                onClick={handleChangePassword}
                disabled={pwdSending || pwdTimeLeft > 0}
                type="button"
              >
                {pwdSending ? 'Enviando...' : (pwdSent ? `Enviado${pwdTimeLeft > 0 ? ` (${pwdTimeLeft}s)` : ''}` : 'Cambiar contraseña')}
              </button>
            </div>
          </div>
        </div>

        {pageError ? <div className="profile-alert is-error">{pageError}</div> : null}

        <div className="profile-section" id="account-info-section">
          <div className="section-header">
            <h3 className="section-title"><span className="section-icon">👤</span> Información de la Cuenta</h3>
          </div>

          {isLoadingProfile ? (
            <div className="profile-loading-state">Cargando información del usuario...</div>
          ) : (
            <form id="account-form" onSubmit={handleSaveAccount}>
              <div className="info-grid">
                <div className="info-item">
                  <label className="info-label" htmlFor="name">Nombre Completo</label>
                  <input className="info-value" id="name" value={formData.name} onChange={handleFieldChange} required />
                </div>

                <div className="info-item">
                  <label className="info-label" htmlFor="email">Correo Electrónico</label>
                  <input className="info-value" id="email" type="email" value={formData.email} onChange={handleFieldChange} required />
                </div>

                <div className="info-item">
                  <label className="info-label" htmlFor="company">Empresa</label>
                  <input className="info-value" id="company" value={formData.company} onChange={handleFieldChange} placeholder="Nombre de empresa" />
                </div>

                <div className="info-item">
                  <label className="info-label" htmlFor="company_rut">RUT Empresa</label>
                  <input className="info-value" id="company_rut" value={formData.company_rut} onChange={handleFieldChange} placeholder="76.123.456-7" />
                </div>

                <div className="info-item">
                  <label className="info-label" htmlFor="rut">RUT Personal</label>
                  <input className="info-value" id="rut" value={formData.rut} onChange={handleFieldChange} placeholder="12.345.678-9" />
                </div>

                <div className="info-item">
                  <label className="info-label" htmlFor="region">Región</label>
                  <input className="info-value" id="region" value={formData.region} onChange={handleFieldChange} placeholder="Metropolitana" />
                </div>

                <div className="info-item">
                  <label className="info-label" htmlFor="city">Ciudad</label>
                  <input className="info-value" id="city" value={formData.city} onChange={handleFieldChange} placeholder="Santiago" />
                </div>

                <div className="info-item info-item-wide">
                  <label className="info-label" htmlFor="address">Dirección</label>
                  <input className="info-value" id="address" value={formData.address} onChange={handleFieldChange} placeholder="Calle, número, comuna" />
                </div>
              </div>

              <div className="profile-summary-grid">
                <div className="profile-summary-card">
                  <span>Tipo de cuenta</span>
                  <strong>{profile.is_company ? 'Empresa' : 'Persona natural'}</strong>
                </div>
                <div className="profile-summary-card">
                  <span>Identificador principal</span>
                  <strong>{primaryIdentifier}</strong>
                </div>
                <div className="profile-summary-card">
                  <span>Origen del registro</span>
                  <strong>{profile.origen || 'No informado'}</strong>
                </div>
              </div>

              <button type="submit" className="save-btn" disabled={isSavingProfile}>
                {isSavingProfile ? 'Guardando...' : '💾 Guardar Cambios'}
              </button>
            </form>
          )}
        </div>

        <div className="profile-section" id="payment-methods-section">
          <div className="section-header">
            <h3 className="section-title"><span className="section-icon">💳</span> Métodos de Pago</h3>
            <button className="add-btn" onClick={() => showToast('La gestión de tarjetas aún no está conectada al backend.', 'info')}>
              + Agregar Tarjeta
            </button>
          </div>
          <div className="payment-cards">
            <div className="payment-card primary">
              <div className="card-badge">Principal</div>
              <div className="card-chip" />
              <div className="card-number">•••• •••• •••• 4532</div>
              <div className="card-footer">
                <div className="card-holder"><div style={{ fontSize: '0.7rem', opacity: 0.8 }}>TITULAR</div><div>{profile.name || 'Usuario Ker'}</div></div>
                <div className="card-expiry"><div style={{ fontSize: '0.7rem', opacity: 0.8 }}>VENCE</div><div>12/25</div></div>
              </div>
            </div>
            <div className="payment-card payment-card-placeholder">
              <div className="payment-placeholder-copy">
                Los metodos de pago reales estarán disponibles en una futura actualización.
              </div>
            </div>
          </div>
        </div>

        <div className="profile-section" id="payment-history-section">
          <div className="section-header"><h3 className="section-title"><span className="section-icon">📊</span> Historial de Pagos</h3></div>
          <table className="payment-table">
            <thead><tr><th>Fecha</th><th>Descripción</th><th>Monto</th><th>Estado</th><th>Factura</th></tr></thead>
            <tbody>
              <tr><td>15 Dic 2024</td><td>Plan Básico - Mensual</td><td>$49.00</td><td><span className="status-badge completed">✓ Pagado</span></td><td><button className="download-btn" onClick={() => showToast('La descarga de facturas aún no está conectada.', 'info')}>📥 Descargar</button></td></tr>
              <tr><td>15 Nov 2024</td><td>Plan Básico - Mensual</td><td>$49.00</td><td><span className="status-badge completed">✓ Pagado</span></td><td><button className="download-btn" onClick={() => showToast('La descarga de facturas aún no está conectada.', 'info')}>📥 Descargar</button></td></tr>
            </tbody>
          </table>
        </div>

        <div className="profile-section" id="active-plan-section">
          <div className="section-header"><h3 className="section-title"><span className="section-icon">💎</span> Plan Activo</h3></div>
          <div className="current-plan">
            <div className="plan-header">
              <div>
                <div className="plan-name" id="basic-plan-name">Plan Básico</div>
                <div style={{ color: '#166534', fontSize: '0.9rem', marginTop: '0.5rem' }}>Plan actual activo</div>
              </div>
              <div className="plan-price">$49<span>/mes</span></div>
            </div>
            <div className="plan-info">
              <div className="plan-info-item"><span className="plan-info-label">Fecha de Inicio</span><span className="plan-info-value">15 Dic 2024</span></div>
              <div className="plan-info-item"><span className="plan-info-label">Próxima Renovación</span><span className="plan-info-value">15 Ene 2025</span></div>
              <div className="plan-info-item"><span className="plan-info-label">Dispositivos Incluidos</span><span className="plan-info-value">50 dispositivos</span></div>
            </div>
            <div className="renewal-progress">
              <div className="progress-label"><span>Periodo actual</span><span>18 de 31 días</span></div>
              <div className="progress-bar"><div className="progress-fill" style={{ width: '58%' }} /></div>
            </div>
          </div>
        </div>

        <div className="profile-section logout-section" id="logout-section">
          <div className="logout-content">
            <div className="logout-icon">🚪</div>
            <h3 className="section-title" style={{ justifyContent: 'center', border: 'none', marginBottom: '1rem' }}>Cerrar Sesión</h3>
            <p className="logout-text">¿Estás seguro de que quieres cerrar sesión? Perderás el acceso a tu cuenta hasta que vuelvas a iniciar sesión.</p>
            <button className="logout-btn" onClick={() => setLogoutModal(true)}>🔓 Cerrar Sesión</button>
          </div>
        </div>

        <div className={`modal-overlay ${logoutModal ? 'active' : ''}`} id="logout-modal">
          <div className="modal-content">
            <div className="modal-icon">⚠️</div>
            <h3 className="modal-title">¿Cerrar Sesión?</h3>
            <p className="modal-text">Estás a punto de cerrar sesión. ¿Deseas continuar?</p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setLogoutModal(false)}>Cancelar</button>
              <button className="modal-btn confirm" onClick={handleLogoutConfirm}>Cerrar Sesión</button>
            </div>
          </div>
        </div>

        <div className={`toast is-${toast.type} ${toast.show ? 'show' : ''}`} id="toast">
          <span>{toast.type === 'error' ? '!' : '✓'}</span>
          <span id="toast-message">{toast.message}</span>
        </div>
      </div>
    </div>
  );
}